import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  User,
  Stethoscope,
  AlertCircle,
  CheckCircle,
  XCircle,
  CalendarPlus
} from 'lucide-react';
import { Appointment, AppointmentStatus, AppointmentType } from '../../types/appointment';
import { API_ENDPOINTS } from '../../config/api';
import { getAuthHeaders } from '../../utils/authUtils';
import AppointmentViewModal from './AppointmentViewModal';

interface AppointmentsListProps {
  petOwnerId?: string;
  petId?: string;
  showHeader?: boolean;
  maxItems?: number;
  showCreateButton?: boolean;
  title?: string;
  onScheduleAppointment?: () => void;  // Callback to open appointment modal
}

const AppointmentsList: React.FC<AppointmentsListProps> = ({
  petOwnerId,
  petId,
  showHeader = true,
  maxItems,
  showCreateButton = false,
  title = "Upcoming Appointments",
  onScheduleAppointment
}) => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (petOwnerId) {
      fetchAppointmentsByOwner();
    }
  }, [petOwnerId]);

  // Helper to get start and end of today in local timezone
  const getTodayRange = () => {
    const now = new Date();
    
    // Start of today (midnight local time)
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    
    // End of today (11:59:59.999 PM local time)
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    
    return { startOfToday, endOfToday };
  };

  const fetchAppointmentsByOwner = async () => {
    if (!petOwnerId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.APPOINTMENTS.LIST_BY_PET_OWNER(petOwnerId), {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch appointments');
      }

      const data = await response.json();

      // Filter for relevant appointments (pending, scheduled, confirmed, in_progress) - excluding completed/cancelled
      let filteredAppointments = data.filter((apt: Appointment) =>
        apt.status === AppointmentStatus.PENDING ||
        apt.status === AppointmentStatus.SCHEDULED ||
        apt.status === AppointmentStatus.CONFIRMED ||
        apt.status === AppointmentStatus.IN_PROGRESS
      );

      // If petId is provided, filter appointments that include this pet
      if (petId) {
        filteredAppointments = filteredAppointments.filter((apt: Appointment) =>
          apt.pets.some(pet => pet.id === petId)
        );
      }

      // Get start of today in local timezone
      const { startOfToday } = getTodayRange();

      // Filter for today's appointments AND any upcoming (future) appointments
      filteredAppointments = filteredAppointments.filter((apt: Appointment) => {
        const appointmentDate = new Date(apt.appointment_date);
        // Include appointments from start of today onwards (today + future)
        return appointmentDate >= startOfToday;
      });

      // Sort by appointment date ASCENDING (earliest first)
      filteredAppointments.sort((a: Appointment, b: Appointment) =>
        new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime()
      );

      // Limit results if maxItems is specified
      if (maxItems) {
        filteredAppointments = filteredAppointments.slice(0, maxItems);
      }

      setAppointments(filteredAppointments);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const { startOfToday, endOfToday } = getTodayRange();
    
    // If appointment is today, just show the time
    if (date >= startOfToday && date <= endOfToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
    }
    
    // Otherwise show full date and time
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatAppointmentTitle = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `Amira Checkup - ${month} ${day} at ${time} `;
  };

  const getTypeIcon = (type: AppointmentType) => {
    switch (type) {
      case AppointmentType.EMERGENCY:
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case AppointmentType.SURGERY:
        return <Stethoscope className="w-4 h-4 text-blue-500" />;
      default:
        return <Calendar className="w-4 h-4 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600 dark:text-dark-text-muted">Loading appointments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-none p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
              {title}
              {appointments.length > 0 && (
                <span className="ml-2 text-sm text-gray-500 dark:text-dark-text-muted">
                  ({appointments.length} appointment{appointments.length !== 1 ? 's' : ''})
                </span>
              )}
            </h3>
          </div>
          {showCreateButton && (
            <button
              onClick={onScheduleAppointment}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold rounded-lg border border-blue-200 transition-colors duration-200"
            >
              <CalendarPlus className="w-5 h-5" />
              <span>Book Appointment</span>
            </button>
          )}
        </div>
      )}

      {appointments.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">No Upcoming Appointments</h3>
          <p className="text-gray-600 dark:text-dark-text-muted">
            {petId ? 'This pet has no upcoming appointments.' : 'No upcoming appointments scheduled.'}
          </p>
          {showCreateButton && (
            <button
              onClick={onScheduleAppointment}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 mt-4 bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold rounded-lg border border-blue-200 transition-colors duration-200"
            >
              <CalendarPlus className="w-5 h-5" />
              <span>Book Appointment</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {appointments.map((appointment) => {
            const appointmentDate = new Date(appointment.appointment_date);
            const { startOfToday, endOfToday } = getTodayRange();
            const isToday = appointmentDate >= startOfToday && appointmentDate <= endOfToday;
            
            return (
              <div
                key={appointment.id}
                onClick={() => {
                  setSelectedAppointment(appointment);
                  setIsModalOpen(true);
                }}
                className={`
                  bg-white dark:bg-dark-bg-secondary rounded-none p-4 cursor-pointer
                  transition-all duration-200
                  hover:shadow-lg hover:-translate-y-0.5 hover:scale-[1.01]
                  ${isToday 
                    ? 'border-2 border-blue-500 dark:border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900/30 shadow-sm hover:ring-4' 
                    : 'border border-gray-200 dark:border-dark-border hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-dark-bg-hover'
                  }
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900 dark:text-dark-text-primary">
                        {appointment.title}
                      </h4>
                      {isToday && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full">
                          <Clock className="w-3 h-3" />
                          Today
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-dark-text-muted">
                      {formatDateTime(appointment.appointment_date)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Appointment Modal - Google Calendar Style */}
      <AppointmentViewModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onEdit={() => {
          if (selectedAppointment) {
            setIsModalOpen(false);
            // RESTful: Navigate to appointments page with edit param
            const appointmentDate = new Date(selectedAppointment.appointment_date);
            // Use local date to avoid timezone issues
            const year = appointmentDate.getFullYear();
            const month = String(appointmentDate.getMonth() + 1).padStart(2, '0');
            const day = String(appointmentDate.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            navigate(`/dashboard/appointments?date=${dateStr}&edit=${selectedAppointment.id}`);
          }
        }}
        onDelete={async () => {
          if (window.confirm('Are you sure you want to delete this appointment?')) {
            try {
              if (!selectedAppointment) return;

              const token = localStorage.getItem('access_token');
              const response = await fetch(API_ENDPOINTS.APPOINTMENTS.CANCEL(selectedAppointment.id), {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });

              if (response.ok) {
                setAppointments(appointments.filter(apt => apt.id !== selectedAppointment.id));
                setIsModalOpen(false);
                setSelectedAppointment(null);
              } else {
                throw new Error('Failed to delete appointment');
              }
            } catch (error) {
              console.error('Error deleting appointment:', error);
              alert('Failed to delete appointment');
            }
          }
        }}
      />
    </div>
  );
};

export default AppointmentsList;
